import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from "recharts";
import { Asterisk, Building2, PlusCircle } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useChartTheme } from "@/shared/hooks/useChartTheme";
import { cn } from "@/shared/lib/cn";
import { resolveChartColor } from "@/shared/lib/resolveChartColor";
import {
  statisticsCompanyExpenses,
  statisticsFrequentDiagnostics,
  statisticsTopHospitals,
} from "../../data/statisticsDashboard";
import { statisticsChartRoutes } from "../../navigation/dashboardRoutes";
import { fetchAdminDashboard } from "../../api/adminApi";
import { getAuthToken } from "../../auth";
import { brand } from "@/shared/lib/safecheckBrand";

type ChartCardProps = {
  title: string;
  icon: ReactNode;
  badge?: string;
  badgeClassName?: string;
  href?: string;
  children: ReactNode;
};

function ChartCard({ title, icon, badge, badgeClassName, href, children }: ChartCardProps) {
  const content = (
    <>
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {icon}
          <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        </div>
        {badge && (
          <span
            className={cn(
              "shrink-0 rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide",
              badgeClassName,
            )}
          >
            {badge}
          </span>
        )}
      </div>
      {children}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="theme-card block p-5 transition-all hover:border-accent/40 hover:shadow-[var(--shadow)]"
      >
        {content}
      </Link>
    );
  }

  return <div className="theme-card theme-card-interactive p-5">{content}</div>;
}

type TopHospitalRow = {
  id: string;
  name: string;
  admissions: number;
  color: string;
};

const barColors = [brand.navy, brand.navyLight, brand.lime, brand.navy, brand.lime];

export function StatisticsInsightsSection() {
  const { t, locale } = useTranslation();
  const { tick, grid, colors, isDark } = useChartTheme();
  const labels = t.safecheck.statistics.insights;
  const [topHospitals, setTopHospitals] = useState<TopHospitalRow[]>(
    statisticsTopHospitals.map((row) => ({
      id: row.id,
      name: row.name,
      admissions: row.admissions,
      color: row.color,
    })),
  );
  const [topYear, setTopYear] = useState(new Date().getFullYear());
  const [usingApi, setUsingApi] = useState(false);

  useEffect(() => {
    if (!getAuthToken()) return;
    let cancelled = false;
    void fetchAdminDashboard()
      .then((api) => {
        if (cancelled) return;
        const year = api.top_hospitals_year ?? new Date().getFullYear();
        const rows = (api.top_hospitals ?? []).map((row, index) => ({
          id: String(row.id),
          name: row.name,
          admissions: row.admissions,
          color: barColors[index % barColors.length],
        }));
        setTopYear(year);
        setTopHospitals(rows);
        setUsingApi(true);
      })
      .catch(() => {
        if (!cancelled) setUsingApi(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const maxAdmissions = Math.max(...topHospitals.map((hospital) => hospital.admissions), 1);
  const topHospitalsTitle = labels.topHospitals.replace("{year}", String(topYear));

  const formatAdmissions = (count: number) =>
    labels.admissionsCount.replace(
      "{count}",
      new Intl.NumberFormat(locale === "fr" ? "fr-FR" : "en-US").format(count),
    );

  return (
    <section className="mt-4">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <ChartCard
          title={labels.frequentDiagnostics}
          href={statisticsChartRoutes.frequentDiagnostics}
          badge={labels.top8}
          badgeClassName="bg-purple-soft text-purple"
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-purple-soft">
              <Asterisk size={16} className="text-purple" />
            </div>
          }
        >
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={statisticsFrequentDiagnostics}
              margin={{ top: 4, right: 8, left: -12, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 11 }}
                hide
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: tick, fontSize: 11 }}
                domain={[0, 1]}
                ticks={[0, 1]}
                width={36}
              />
              {statisticsFrequentDiagnostics.length > 0 && (
                <Bar dataKey="value" fill={colors.navy} radius={[4, 4, 0, 0]} maxBarSize={32} />
              )}
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard
          title={labels.companyExpenses}
          href={statisticsChartRoutes.companyExpenses}
          badge={labels.year2026}
          badgeClassName="bg-teal-soft text-teal"
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-soft">
              <Building2 size={16} className="text-teal" />
            </div>
          }
        >
          {statisticsCompanyExpenses.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={statisticsCompanyExpenses} margin={{ top: 4, right: 8, left: -12, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={grid} vertical={false} />
                <XAxis dataKey="key" axisLine={false} tickLine={false} tick={{ fill: tick, fontSize: 11 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: tick, fontSize: 11 }} width={36} />
                <Bar dataKey="value" fill={colors.lime} radius={[4, 4, 0, 0]} maxBarSize={32} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="min-h-[220px]" aria-hidden />
          )}
        </ChartCard>

        <ChartCard
          title={topHospitalsTitle}
          href={statisticsChartRoutes.topHospitals}
          badge={usingApi ? String(topYear) : labels.year2026}
          badgeClassName="bg-yellow-soft text-yellow"
          icon={
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-yellow-soft">
              <PlusCircle size={16} className="text-yellow" />
            </div>
          }
        >
          <div className="space-y-5 pt-2">
            {topHospitals.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted">{labels.emptyTopHospitals}</p>
            ) : (
              topHospitals.map((hospital, index) => {
                const widthPct = Math.max((hospital.admissions / maxAdmissions) * 100, 2);

                return (
                  <div key={hospital.id}>
                    <div className="mb-2 flex items-center justify-between gap-3">
                      <span className="truncate text-sm font-medium text-foreground">{hospital.name}</span>
                      <span className="shrink-0 text-xs text-muted">
                        {formatAdmissions(hospital.admissions)}
                      </span>
                    </div>
                    <div
                      className={cn(
                        "w-full overflow-hidden rounded-full bg-card-muted",
                        index === 0 ? "h-3" : "h-1.5",
                      )}
                    >
                      <div
                        className="h-full rounded-full transition-all"
                        style={{
                          width: `${widthPct}%`,
                          backgroundColor: resolveChartColor(hospital.color, isDark),
                        }}
                      />
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ChartCard>
      </div>
    </section>
  );
}
